<template>
  <div class="template-preview">
    <div class="template-preview__title">
      <input
        type="checkbox"
        class="cursor-pointer"
        :checked="checked"
        @change="handleTemplateSelect(template.instance_id)"
      >
      <div
        class="d-flex cursor-pointer align-items-center"
        @click="handleLayersVisibility"
      >
        <b-icon
          icon="triangle-fill"
          class="mr-2"
          :class="{'closed': !layersVisible, 'opened': layersVisible}"
        />
        {{ trimName(template.name) }}
      </div>
    </div>
    <b-collapse :visible="layersVisible">
      <span
        v-if="isLayersPreparing"
        class="spinner-border spinner-border-sm mx-4 my-2"
        role="status"
        aria-hidden="true"
      />
      <div
        v-else
        class="template-preview__layers-list"
      >
        <div
          v-for="(layer) in filteredLayers"
          :key="layer.id"
          class="template-preview__layer"
        >
          <input
            type="checkbox"
            class="cursor-pointer"
            :checked="selectedLayers.find(_layer => _layer.id === layer.id)"
            @change="handleLayerSelect(layer)"
          >
          <div class="template-preview__layer-preview">
            <div
              v-if="layer.properties.subType === LAYER_SUBTYPE.TEXT"
              class="template-preview__layer-preview--text"
            >
              <b-icon
                icon="textarea-t"
                class="d-block"
              />
            </div>
            <div
              v-if="layer.properties.subType === LAYER_SUBTYPE.IMAGE"
              class="template-preview__layer-preview--image"
            >
              <img
                :src="layer.properties.src"
                alt="layer-preview"
              >
            </div>
          </div>
          {{ trimName(layer.name) }}
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import {trimFileName} from "@frontend/services/helpers";
import {SUPPORTED_FIELDS, LAYER_SUBTYPE} from "./constants";


export default {
  name: 'PreviewTemplate',
  props: {
    template: {
      required: true,
      type: Object,
    },
    checked: {
      type: Boolean,
      default: false
    },
    selectedLayers: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      LAYER_SUBTYPE,
      layersVisible: false,
      layers: [],
      isLayersPreparing: true
    };
  },
  computed: {
    templateName() {
      return trimFileName(this.template.name, 36);
    },
    filteredLayers() {
      return this.layers.filter(layer => layer.properties.subType === LAYER_SUBTYPE.IMAGE && layer.properties.src);
    }
  },
  async mounted() {
    const preparedLayers = await Promise.all([
      this.prepareBaseFields(),
      this.prepareAdditionalFields()
    ]);
    this.layers = preparedLayers.flat();
    this.isLayersPreparing = false;
  },
  methods: {
    trimName(name) {
      return trimFileName(name, 21)
    },
    handleLayersVisibility() {
      this.layersVisible = !this.layersVisible;
    },
    handleTemplateSelect() {
      this.$emit('template-select', this.template.instance_id)
    },
    handleLayerSelect(layer) {
      this.$emit('layer-select', layer);
    },
    async prepareBaseFields() {
      const templateSettings = JSON.parse(this.template.settings);
      const baseFields = this.template.template.fields.filter(field => SUPPORTED_FIELDS.some(_field => _field.type === field.type));
      const layers = [];
      const counters = {};

      for (const baseField of baseFields) {
        const field = { ...baseField };
        const mapper = SUPPORTED_FIELDS.find(_mapper => _mapper.type === field.type);

        if (!mapper) {
          continue;
        }

        if (mapper.indexable && !counters[field.type]) {
          counters[baseField.type] = 0;
        }

        field.properties = await mapper.map({
          templateSettings: templateSettings,
          field: field,
          layoutId: this.template.layout_id,
          ...(mapper.indexable ? { index: counters[field.type] } : {})
        });

        if (mapper.indexable) {
          counters[field.type]++;
        }

        layers.push(field);
      }

      return layers;
    },
    prepareAdditionalFields() {
      // @TODO
      return [];
    }
  }
}
</script>

<style lang="scss">
.template-preview {
  display: flex;
  flex-direction: column;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    border-top: solid 1px #505050;
    border-bottom: solid 1px #505050;
    padding: 4px;
  }

  &__layers-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 18px;
  }

  &__layer {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__layer-preview {
    border: solid 1px #bebebe;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;

    &--text {
      display: flex;
      align-items: center;
      justify-content: center;
      width: inherit;
      height: inherit;
      font-size: 24px;
    }

    &--image {
      width: inherit;
      height: inherit;

      img {
        width: inherit;
        height: inherit;
        object-fit: contain;
      }
    }
  }

  .closed, .opened {
    font-size: 11px;
    transition: transform 0.5s;
  }

  .closed {
    transform: rotate(90deg);
  }

  .opened {
    transform: rotate(180deg);
  }
}
</style>
